const localeMap: Record<string, string> = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
  zh: 'zh-CN',
  fr: 'fr-FR',
}

/**
 * Shared utility functions for shop and account pages
 */
export function useShopUtils() {
  const { locale } = useI18n()

  const intlLocale = computed(() => localeMap[locale.value] || 'es-ES')

  /**
   * Format a price in EUR currency (locale-aware)
   */
  const formatPrice = (price: number | null): string => {
    if (price === null || price === undefined) return '-'
    return new Intl.NumberFormat(intlLocale.value, {
      style: 'currency',
      currency: 'EUR',
    }).format(price)
  }

  /**
   * Format a date string with full month and optional time
   */
  const formatDate = (dateStr: string, includeTime = false): string => {
    const date = new Date(dateStr)
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
    if (includeTime) {
      options.hour = '2-digit'
      options.minute = '2-digit'
    }
    return date.toLocaleDateString(intlLocale.value, options)
  }

  /**
   * Format a date string with short month and time (for timelines)
   */
  const formatShortDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(intlLocale.value, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  /**
   * Calculate discount percentage between sale price and original price
   */
  const getDiscount = (price: number, originalPrice: number): number => {
    if (!originalPrice || originalPrice <= price) return 0
    return Math.round((1 - price / originalPrice) * 100)
  }

  /**
   * Get Tailwind CSS classes for a condition grade badge
   * Supports backend 7-level grading system
   */
  const getConditionColor = (condition: string): string => {
    const colors: Record<string, string> = {
      sealed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      like_new: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
      excellent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
      very_good: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border-teal-200 dark:border-teal-800',
      good: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      fair: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
      acceptable: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
    }
    return colors[condition] || colors.good
  }

  /**
   * Format a number with locale-aware thousand separators
   */
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat(intlLocale.value).format(num)
  }

  return {
    formatPrice,
    formatDate,
    formatShortDate,
    formatNumber,
    getDiscount,
    getConditionColor,
  }
}
